import {
  GroupOptionToggle,
  ProductGroupMultiselectFilter,
  SideFilters,
  ToggleSwitch,
} from '@retail/components';
import { useProductsTFunction } from '@retail/products/i18n';
import { DiscountGroup, FilterOptions, GroupOption, ProductGroup } from '@retail/products/types';
import { SelectDiscountGroups, SelectSuppliersAutocomplete } from '../MgSupplierItem';
import { MgSupplierItemSearchField } from '../MgItem';
import { SearchFilters, searchFilters } from '@retail/products/utils';
import { useCallback } from 'react';
import { useFilters } from '@retail/products/hooks';

interface Props {
  filterOptions: FilterOptions;
  isFetching: boolean;
  showFilters: boolean;
  setPage: (page: number) => void;
  showProductOwner: boolean;
}

export function ProductSearchFilters({
  filterOptions,
  isFetching,
  showFilters,
  setPage,
  showProductOwner,
}: Props) {
  const t = useProductsTFunction();
  const { filters, updateFilter, setFilters } = useFilters<SearchFilters>(searchFilters);

  type FilterUpdateParam = Parameters<typeof setFilters>[0];
  const handleSetFilter = useCallback(
    (filterOption: FilterUpdateParam) => {
      setFilters(filterOption);
      setPage(0);
    },
    [setFilters, setPage]
  );

  const {
    supplierNumbers,
    discountGroupCodes,
    productGroupNumbers,
    searchQuery,
    inAssortment,
    purchasePrice,
    discontinuedItem,
    fromMainSupplier,
  } = filters;

  const { suppliers, productGroupHierarchy, discountGroups } = filterOptions;

  return (
    <SideFilters showFilters={showFilters}>
      <GroupOptionToggle
        label={t('products.search.itemsInMyAssortment')}
        value={inAssortment}
        labelDirection="horizontal"
        justifyContent="space-between"
        fullWidth={false}
        onChange={(option: GroupOption) =>
          handleSetFilter({ inAssortment: option === 'UNSPECIFIED' ? undefined : option })
        }
      />
      <GroupOptionToggle
        label={t('products.search.itemsWithPurchasePrice')}
        value={purchasePrice}
        labelDirection="horizontal"
        justifyContent="space-between"
        fullWidth={false}
        onChange={(option: GroupOption) =>
          handleSetFilter({ purchasePrice: option === 'UNSPECIFIED' ? undefined : option })
        }
      />
      <GroupOptionToggle
        label={t('products.mgItem.details.excludeDiscontinuedItems')}
        value={discontinuedItem}
        labelDirection="horizontal"
        justifyContent="space-between"
        fullWidth={false}
        onChange={(option: GroupOption) =>
          handleSetFilter({ discontinuedItem: option === 'UNSPECIFIED' ? undefined : option })
        }
      />
      {showProductOwner && (
        <ToggleSwitch
          justifyContent="space-between"
          toggle={fromMainSupplier}
          setToggle={() => handleSetFilter({ fromMainSupplier: !fromMainSupplier })}
          text={t('products.search.onlyProductOwners')}
        />
      )}
      <SelectSuppliersAutocomplete
        suppliers={suppliers}
        selectedSupplierNumbers={supplierNumbers.map(Number)}
        isFetching={isFetching}
        selectSuppliers={(supplierNumbers: number[]) =>
          handleSetFilter({ supplierNumbers: supplierNumbers.map(String) })
        }
      />
      {supplierNumbers.length > 0 && (
        <SelectDiscountGroups
          discountGroups={discountGroups}
          selectedDiscountGroups={discountGroupCodes.map(Number)}
          getDiscountGroupKey={({ id }: DiscountGroup) => id}
          disabled={isFetching}
          selectDiscountGroups={(groupIds: number[]) =>
            handleSetFilter({ discountGroupCodes: groupIds.map(String) })
          }
        />
      )}

      <MgSupplierItemSearchField
        searchTerm={searchQuery ?? ''}
        isDisabled={isFetching}
        size="small"
        onSubmitSearch={(query: string) => handleSetFilter({ searchQuery: query })}
      />
      <ProductGroupMultiselectFilter
        productGroups={productGroupHierarchy}
        selectedProductGroups={productGroupNumbers}
        selectProductGroup={(groupNumber: string) => {
          setPage(0);
          updateFilter('productGroupNumbers', groupNumber);
        }}
        getCount={(productGroup: ProductGroup) => productGroup.count}
      />
    </SideFilters>
  );
}
